import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import BunkerBargeIMOField from "@features/vessel-call/vessel-call-status/PortServices/form/bunker-barge/BunkerBargeIMOField";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { usePortServicesFormStore } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useOffshoreServiceCompanies } from "@hooks/useOffshoreServiceCompanies";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type UnitsOfMeasurement } from "@services/vessel-call/types";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useBerthAnchorageAreaOptions } from "../../hooks/useBerthAnchorageAreaOptions";
import { useFromToLocationOptions } from "../../hooks/useFromToLocationOptions";

type DeSlopingFieldsOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  currentVesselImoNumber?: string;
  selectedPortCode?: string;
};

const DeSlopingFields = ({ langBase }: { langBase: string }) => {
  const { formData, updateFormData, currentVesselImoNumber, selectedPortCode } =
    useOutletContext<DeSlopingFieldsOutlet>();
  const { t } = useTranslation();
  const { serviceCategoryCode, serviceTypeCode, serviceSubTypeCode } = usePortServicesFormStore();

  const { options: companies = [] } = useOffshoreServiceCompanies(
    {
      portServiceCategoryCode: serviceCategoryCode,
      portServiceTypeCode: serviceTypeCode,
      portServiceSubtypeCode: serviceSubTypeCode,
    },
    "bff-vessel",
  );

  const { options: unitsOfMeasurement } = useFormattedReference<UnitsOfMeasurement>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-vessel-call",
  );

  const deSlopingCompanyOptions = useMemo(
    () =>
      companies
        .map(({ companyNameEnglish, companyCode }) => ({
          value: companyNameEnglish ?? companyCode ?? "",
          label: companyNameEnglish ?? companyCode ?? "",
        }))
        .filter((option) => option.value)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [companies],
  );

  const unitOfMeasurementOptions = useMemo(
    () =>
      unitsOfMeasurement
        .filter((u) => u.isCargoUnit)
        .map((u) => ({ label: u.unitDescription, value: u.unitDescription }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [unitsOfMeasurement],
  );

  const selectedLocation = formData.getValues("deSlopingType");
  const { locations, fromToLocationOptions } = useFromToLocationOptions(
    ({ portCode }) => portCode === selectedPortCode,
  );
  const currentLocation = locations.find(
    (location) => location.fromToLocationCode === selectedLocation,
  );
  const {
    options: berthAnchorageAreaOptions,
    isLoading: isBerthAnchorageLoading,
    startLocationNavigation,
  } = useBerthAnchorageAreaOptions(
    currentLocation?.fromToLocationCode,
    currentLocation?.location,
    "deSlopingType",
  );

  return (
    <FormProvider {...formData}>
      <div className="grid grid-cols-4 gap-2">
        <div className="order-1 col-span-2">
          <FormItem<PortServicesFormData, "deSlopingType">
            name="deSlopingType"
            type="autocomplete"
            options={fromToLocationOptions}
            label={t(`${langBase}.fields.de-sloping-type`)}
            testId="de-sloping-type"
            onFieldChange={(key, value) => {
              const nextLocation = locations.find(
                (location) => location.fromToLocationCode === value,
              );
              startLocationNavigation(nextLocation?.fromToLocationCode, nextLocation?.location);
              updateFormData(key, value);
              formData.setValue("berthOrAnchorageCode", "", { shouldDirty: true });
              updateFormData("berthOrAnchorageCode", "");
            }}
          />
        </div>
        <div className="order-1 col-span-2">
          <FormItem<PortServicesFormData, "berthOrAnchorageCode">
            name="berthOrAnchorageCode"
            type="autocomplete"
            options={berthAnchorageAreaOptions}
            label={t(`${langBase}.fields.berth-anchorage-area-location`)}
            testId="berth-or-anchorage-code"
            onFieldChange={updateFormData}
            disabled={isBerthAnchorageLoading || !selectedLocation}
          />
        </div>
        <div
          data-testid="de-sloping-bunker-barge-layout"
          className="contents [&>*:first-child]:order-2 [&>*:last-child]:order-4 [&>*:last-child]:col-start-1"
        >
          <BunkerBargeIMOField
            formData={formData}
            updateFormData={updateFormData}
            langBase={langBase}
            currentVesselImoNumber={currentVesselImoNumber}
            selectedPortCode={selectedPortCode}
            invalidMessageKey="form-validation.valid-bunker-barge-imo-required"
          />
        </div>

        <div className="order-3 col-span-2">
          <FormItem<PortServicesFormData, "deSlopingCompanyName">
            name="deSlopingCompanyName"
            type="autocomplete"
            options={deSlopingCompanyOptions}
            label={t(`${langBase}.fields.de-sloping-company-name`)}
            testId="deSlopingCompanyName"
            onFieldChange={updateFormData}
          />
        </div>
        <div className="order-5 col-span-1 col-start-1">
          <FormItem<PortServicesFormData, "quantity">
            name="quantity"
            type="number"
            label={t(`${langBase}.fields.quantity`)}
            testId="quantity"
            onFieldChange={updateFormData}
            maxDigits={15}
            canBeginWithZero={false}
          />
        </div>
        <div className="order-6 col-span-1">
          <FormItem<PortServicesFormData, "unitOfMeasurement">
            name="unitOfMeasurement"
            type="autocomplete"
            options={unitOfMeasurementOptions}
            label={t(`${langBase}.fields.unit-of-measurement`)}
            testId="unit-of-measurement"
            onFieldChange={updateFormData}
          />
        </div>

        <div className="order-7 col-span-2 col-start-1">
          <FormItem<PortServicesFormData, "rts">
            name="rts"
            type="date-picker"
            label={t(`${langBase}.fields.rts`)}
            testId="portServices-rts"
            onFieldChange={updateFormData}
            timePicker
            combinedInput
            dateConstraint="not-past"
            minDate={new Date()}
            maxDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default DeSlopingFields;
